import React, { useState } from 'react';
import { motion } from 'motion/react';
import { Linkedin, Facebook, Instagram, Mail, Link as LinkIcon, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';

function XLogo(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor" {...props}>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 22.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

interface SocialShareProps {
  title: string;
  url?: string;
}

export function SocialShare({ title, url }: SocialShareProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  
  const shareToLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, 'linkedin-share', 'width=800,height=600');
  };
  
  const shareToTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`, 'twitter-share', 'width=800,height=600');
  };

  const shareToFacebook = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, 'facebook-share', 'width=800,height=600');
  };

  const shareToInstagram = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      toast.success('Link copied to clipboard! You can now paste it into Instagram.');
    });
  };

  const shareToEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent("Check this out: " + shareUrl)}`;
  };
  
  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareButtons = [
    { icon: Linkedin, action: shareToLinkedIn, label: t('share.linkedin', 'Share on LinkedIn') },
    { icon: XLogo, action: shareToTwitter, label: t('share.twitter', 'Share on X (Twitter)') },
    { icon: Facebook, action: shareToFacebook, label: t('share.facebook', 'Share on Facebook') },
    { icon: Instagram, action: shareToInstagram, label: t('share.instagram', 'Share on Instagram') },
    { icon: Mail, action: shareToEmail, label: t('share.email', 'Share via Email') },
    { icon: copied ? Check : LinkIcon, action: copyToClipboard, label: copied ? 'Copied!' : 'Copy Link', extraClasses: copied ? 'text-rule-3 border-rule-3 shadow-sm' : '' },
  ];

  return (
    <>
      {/* Desktop Floating Version */}
      <motion.div 
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="fixed left-4 xl:left-8 top-1/2 -translate-y-1/2 z-40 hidden lg:flex flex-col items-center gap-4"
      >
        <span className="text-[10px] uppercase tracking-widest font-bold text-ink/70/50 rotate-180" style={{ writingMode: 'vertical-rl' }}>
          {t('share.share', 'Share')}
        </span>
        <div className="w-[1px] h-12 bg-ground-deep my-2" />
        
        {shareButtons.map((btn, idx) => {
          const Icon = btn.icon;
          return (
            <button 
              key={idx}
              onClick={btn.action}
              className={`w-10 h-10 rounded-full border border-ground-deep bg-white flex items-center justify-center text-ink/70 hover:text-rule-3 hover:border-rule-3 hover:shadow-md transition-all group ${btn.extraClasses || ''}`}
              aria-label={btn.label}
              title={btn.label}
            >
              <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
          );
        })}
      </motion.div>

      {/* Mobile Inline Version */}
      <div className="flex lg:hidden items-center gap-4 mt-12 pt-8 border-t border-ground-deep/50">
        <span className="text-xs uppercase tracking-widest font-bold text-ink/70/60">
          {t('share.share', 'Share')}
        </span>
        <div className="flex flex-wrap gap-3">
          {shareButtons.map((btn, idx) => {
            const Icon = btn.icon;
            return (
              <button 
                key={idx}
                onClick={btn.action}
                className={`w-10 h-10 rounded-full border border-ground-deep bg-white flex items-center justify-center text-ink/70 hover:text-rule-3 hover:border-rule-3 hover:shadow-md transition-all group ${btn.extraClasses || ''}`}
                aria-label={btn.label}
                title={btn.label}
              >
                <Icon className="w-4 h-4 group-hover:scale-110 transition-transform" />
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
